import { IntegrationEndpointVariable } from '@components/integrations/integrations-types';
import {
  parseDynamicOption,
  stringifyDynamicOption,
} from '@components/integrations/lists/dynamic-options-utils';
import { SelectOption } from '@components/select';

export type DynamicSelectOption = { value: string; label: string; endpointValue: string };

// The saved value carries its own label, so keep it available as an option even
// before the fetched list loads — otherwise the tag shows the stored value.
export const includeSavedOption = (
  fetchedOptions: DynamicSelectOption[],
  savedValue: unknown,
): DynamicSelectOption[] => {
  const parsed = typeof savedValue === 'string' ? parseDynamicOption(savedValue) : undefined;
  if (!parsed) return fetchedOptions;
  if (fetchedOptions.some((option) => option.endpointValue === parsed.value)) return fetchedOptions;
  return [
    { value: stringifyDynamicOption(parsed), label: parsed.label, endpointValue: parsed.value },
    ...fetchedOptions,
  ];
};

export const extractRequestData = (
  data: Record<string, any>,
  variables: IntegrationEndpointVariable[],
) => {
  const prefix = 'requestData__';
  const requestData: { [key: string]: any } = {};

  const setArrayValues = (obj: any, path: (string | number)[], value: any) => {
    let current = obj;
    for (let i = 0; i < path.length - 1; i++) {
      const part = path[i];
      if (!current[part]) {
        if (isNaN(Number(path[i + 1]))) {
          current[part] = {};
        } else {
          current[part] = [];
        }
      }
      current = current[part];
    }
    current[path[path.length - 1]] = value;
  };

  Object.keys(data).forEach((key) => {
    if (key.startsWith(prefix)) {
      const newKey = key.substring(prefix.length);
      const value = data[key];
      if (value === undefined) return;
      let processedValue = value;
      if (typeof value !== 'object') {
        processedValue = { value, variables: {} };
      }
      const variable = variables.find((v) => v.name === newKey);
      if (variable) {
        if (variable.type === 'array_of_strings' && Array.isArray(value)) {
          const stringValues = (value as Array<string | { value?: string }>)
            .map((item) => (typeof item === 'string' ? item : (item?.value ?? '')))
            .filter((entry) => entry.length > 0);
          requestData[newKey] = { value: stringValues, variables: {} };
        } else {
          requestData[newKey] = processedValue;
        }
      } else {
        const path = newKey.split('__').map((part) => (isNaN(Number(part)) ? part : Number(part)));
        setArrayValues(requestData, path, processedValue);
      }
    }
  });

  return requestData;
};

export const mapSavedValuesToSelectOptions = (
  savedValues: unknown,
  options: SelectOption[],
): SelectOption[] => {
  if (!Array.isArray(savedValues)) return [];
  return savedValues
    .map((savedValue) => options.find((option) => option.value === savedValue))
    .filter((option): option is SelectOption => option !== undefined);
};
